import { Column, Row } from 'app/design/primitives/box';
import { ControlledInput } from 'app/design/primitives/input/controlled';
import { Text } from 'app/design/primitives/text';

import { useNavigation } from 'app/hooks/navigation';
import { Trans, useTranslation } from 'react-i18next';

import type { Profile } from 'app/network/profile';
import { FeatureFlag, isFeatureEnabled } from 'app/utils/helpers';
import { Pressable } from 'react-native';

import { useAllAddresses } from 'app/network/address';
import { useAtomValue } from 'jotai';
import { userInfoAtom } from 'app/atoms';
import type { ProfileInformationFormValues } from 'app/screens/profile/my-information/schema';
import { AddressCard } from './address-card';
import { createTheme as createDSTheme } from 'app/shopify';
import { IconChevronRight, IconLock } from '@tabler/icons-react-native';
import { ICON_STROKE_WIDTH } from 'app/utils/constants';
import type { Control } from 'react-hook-form';
const theme = createDSTheme();

interface SectionProps {
  control: Control<ProfileInformationFormValues>;
  profile?: Profile;
}

export function AccountInformationSection({ control, profile }: SectionProps) {
  const navigation = useNavigation();
  const { t } = useTranslation('my-information');
  const userInfo = useAtomValue(userInfoAtom);

  const addressesQuery = useAllAddresses();
  const selectedAddress = addressesQuery.data?.[0];

  return (
    <Column
      gap="spacing.general.x4"
      style={{
        backgroundColor: theme.colors['surface.default'],
        marginHorizontal: theme.spacing['spacing.general.x4'],
        padding: theme.spacing['spacing.general.x4'],
        borderRadius: theme.borderRadii['radius.md'],
        borderWidth: 1,
        borderColor: theme.colors['surface.disabled'],
      }}
    >
      <Column gap="spacing.general.x1">
        <Text variant="bodyLarge" fontWeight="700" color="text.primary">
          <Trans i18nKey="credentialInformationTitle" ns="my-information" defaults="Account & security" />
        </Text>
        <Text variant="bodyBase" color="text.placeholder">
          <Trans
            components={{ strong: <Text fontWeight="700" color="text.placeholder" /> }}
            i18nKey="credentialInformationText"
            ns="my-information"
          />
        </Text>
      </Column>

      <ControlledInput
        {...{ control }}
        autoComplete="tel"
        editable={Boolean(profile)}
        label={t('phoneFieldLabel', {
          ns: 'my-information',
        })}
        name="phone"
        textContentType="telephoneNumber"
      />

      <ControlledInput
        {...{ control }}
        autoComplete="email"
        editable={Boolean(profile)}
        label={t('emailFieldLabel')}
        name="email"
        textContentType="emailAddress"
      />

      <Column gap="spacing.general.x2">
        <Row gap="spacing.general.x1">
          <Text color="text.primary" fontWeight="700" variant="bodyBase">
            {t('passwordFieldLabel')}
          </Text>
        </Row>

        <Pressable onPress={() => navigation.navigate('change-password', undefined, { pop: true })}>
          <Row
            alignItems="center"
            gap="spacing.general.x2"
            style={{
              borderWidth: 1,
              borderColor: theme.colors['border.secondary'],
              borderRadius: theme.borderRadii['radius.md'],
              paddingHorizontal: theme.spacing['spacing.general.x2'],
              paddingVertical: theme.spacing['spacing.general.x4'],
            }}
          >
            <IconLock strokeWidth={ICON_STROKE_WIDTH} />
            <Text color="text.placeholder" flex={1} numberOfLines={1} variant="bodyBase">
              {t('passwordFieldCTA')}
            </Text>
            <IconChevronRight strokeWidth={ICON_STROKE_WIDTH} />
          </Row>
        </Pressable>
      </Column>

      {isFeatureEnabled(FeatureFlag.justOneClub, userInfo?.featureFlags) && selectedAddress ? (
        <Column gap="spacing.general.x2">
          <Row alignItems="center" justifyContent="space-between">
            <Text color="text.primary" fontWeight="700" variant="bodyBase">
              {t('visibilityRadiusSection.title')}
            </Text>
          </Row>
          <AddressCard {...selectedAddress} />
        </Column>
      ) : null}
    </Column>
  );
}
